import React, { useMemo, useState, useEffect } from "react";
import ColorInput from './ColorInput';
import "./ContainerFrameEditor.css";
/**
 * ContainerFrameEditor (strict schema, drop-in)
 * EXACT keys only:
 *  borderStyle, borderWidth, borderColor, borderRadius,
 *  paddingTop, paddingRight, paddingBottom, paddingLeft
 *
 * Padding now supports px or %:
 *  - For px we store numbers (e.g., 24)
 *  - For % we store strings (e.g., "10%")
 */
export default function ContainerFrameEditor({ value, onChange, onSave }) {
    const v = value || {};
    const [linkPadding, setLinkPadding] = useState(false);

    // Detect current unit from the first available padding value
    const detectUnit = (val) =>
        typeof val === "string" && /\%$/.test(val) ? "%" : "px";

    const initialUnit = useMemo(() => {
        const candidates = [v.paddingTop, v.paddingRight, v.paddingBottom, v.paddingLeft];
        const first = candidates.find((x) => x !== undefined && x !== null);
        return detectUnit(first);
    }, [v.paddingTop, v.paddingRight, v.paddingBottom, v.paddingLeft]);

    const [paddingUnit, setPaddingUnit] = useState(initialUnit || "px");

    // Keep unit in sync if external value changes radically
    useEffect(() => {
        setPaddingUnit(initialUnit || "px");
    }, [initialUnit]);

    const set = (k, val) => onChange({ ...v, [k]: val });

    // Helpers to parse displayed numbers from px/%
    const getNumeric = (val) => {
        if (typeof val === "string") return parseFloat(val) || 0;
        if (typeof val === "number" && isFinite(val)) return val;
        return 0;
    };

    // Convert a raw numeric input into stored value according to unit
    const toStored = (num) => (paddingUnit === "px" ? num : `${num}%`);

    // Read current values as numbers for inputs
    const padTopNum = getNumeric(v.paddingTop);
    const padRightNum = getNumeric(v.paddingRight);
    const padBottomNum = getNumeric(v.paddingBottom);
    const padLeftNum = getNumeric(v.paddingLeft);

    const linkedPaddingValue =
        padTopNum || padRightNum || padBottomNum || padLeftNum || 0;

    const setPaddingAll = (num) => {
        onChange({
            ...v,
            paddingTop: toStored(num),
            paddingRight: toStored(num),
            paddingBottom: toStored(num),
            paddingLeft: toStored(num),
        });
    };

    // When unit changes, do NOT guess physical conversion; just re-store with the same numeric magnitudes
    // so future edits persist in the new unit. This avoids surprises from container-relative math.
    const handleUnitChange = (nextUnit) => {
        if (nextUnit === paddingUnit) return;
        // keep numeric magnitudes; just swap representation
        const t = getNumeric(v.paddingTop);
        const r = getNumeric(v.paddingRight);
        const b = getNumeric(v.paddingBottom);
        const l = getNumeric(v.paddingLeft);
        const toVal = (n) => (nextUnit === "px" ? n : `${n}%`);
        onChange({
            ...v,
            paddingTop: toVal(t),
            paddingRight: toVal(r),
            paddingBottom: toVal(b),
            paddingLeft: toVal(l),
        });
        setPaddingUnit(nextUnit);
    };

    return (
        <div className="container-frame-editor">
            {/* Border Style */}
            <div className="form-row">
                <label>Border Style</label>
                <select
                    value={v.borderStyle || "solid"}
                    onChange={(e) => set("borderStyle", e.target.value)}
                >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                    <option value="double">Double</option>
                    <option value="none">None</option>
                </select>
            </div>

            {/* Border Width / Color */}
            <div className="form-row grid-2">
                <div>
                    <label>Border Width (px)</label>
                    <input
                        type="number"
                        min="0"
                        value={v.borderWidth ?? 0}
                        onChange={(e) =>
                            set("borderWidth", parseInt(e.target.value || "0", 10))
                        }
                    />
                </div>
                <div>
                    <label>Border Color</label>
                    <ColorInput
                        value={v.borderColor || "#E5E7EB"}
                        onChange={(e) => set("borderColor", e.target.value)}
                    />
                </div>
            </div>

            {/* Border Radius */}
            <div className="form-row">
                <label>Border Radius (px)</label>
                <input
                    type="number"
                    min="0"
                    value={v.borderRadius ?? 0}
                    onChange={(e) =>
                        set("borderRadius", parseInt(e.target.value || "0", 10))
                    }
                />
            </div>

            {/* Padding Unit */}
            <div className="form-row">
                <label>Padding Unit</label>
                <div style={{ display: "flex", gap: 8 }}>
                    <label className="checkbox">
                        <input
                            type="radio"
                            name="padding-unit"
                            checked={paddingUnit === "px"}
                            onChange={() => handleUnitChange("px")}
                        />
                        <span>px</span>
                    </label>
                    <label className="checkbox">
                        <input
                            type="radio"
                            name="padding-unit"
                            checked={paddingUnit === "%"}
                            onChange={() => handleUnitChange("%")}
                        />
                        <span>%</span>
                    </label>
                </div>
            </div>

            {/* Link all paddings */}
            <div className="form-row">
                <label className="checkbox">
                    <input
                        type="checkbox"
                        checked={linkPadding}
                        onChange={(e) => setLinkPadding(e.target.checked)}
                    />
                    <span>Link all paddings</span>
                </label>
            </div>

            {/* Padding Inputs */}
            {linkPadding ? (
                <div className="form-row">
                    <label>
                        Padding ({paddingUnit})
                    </label>
                    <input
                        type="number"
                        min="0"
                        value={linkedPaddingValue}
                        onChange={(e) =>
                            setPaddingAll(parseFloat(e.target.value || "0"))
                        }
                    />
                </div>
            ) : (
                <div className="form-row grid-2">
                    <div>
                        <label>Padding Top ({paddingUnit})</label>
                        <input
                            type="number"
                            min="0"
                            value={padTopNum}
                            onChange={(e) =>
                                set("paddingTop", toStored(parseFloat(e.target.value || "0")))
                            }
                        />
                    </div>
                    <div>
                        <label>Padding Right ({paddingUnit})</label>
                        <input
                            type="number"
                            min="0"
                            value={padRightNum}
                            onChange={(e) =>
                                set(
                                    "paddingRight",
                                    toStored(parseFloat(e.target.value || "0"))
                                )
                            }
                        />
                    </div>
                    <div>
                        <label>Padding Bottom ({paddingUnit})</label>
                        <input
                            type="number"
                            min="0"
                            value={padBottomNum}
                            onChange={(e) =>
                                set(
                                    "paddingBottom",
                                    toStored(parseFloat(e.target.value || "0"))
                                )
                            }
                        />
                    </div>
                    <div>
                        <label>Padding Left ({paddingUnit})</label>
                        <input
                            type="number"
                            min="0"
                            value={padLeftNum}
                            onChange={(e) =>
                                set("paddingLeft", toStored(parseFloat(e.target.value || "0")))
                            }
                        />
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="form-actions" style={{ marginTop: 12 }}>
                <button className="muulti-button" onClick={onSave}>
                    Save Container
                </button>
            </div>
        </div>
    );
}
